export const useBonus = () => {
    const user = useUser();
    const { auth } = useLoginState();
    const isRefreshingUser = ref(false);

    const ensureUserLoaded = async () => {
        if (!import.meta.client) return;
        if (!auth.value) return;
        if (user.user.value) return;
        if (isRefreshingUser.value) return;

        isRefreshingUser.value = true;
        try {
            await user.refreshUser();
        } catch (error) {
            console.warn('[useBonus] Failed to refresh user for bonus badge', error);
        } finally {
            isRefreshingUser.value = false;
        }
    };

    watch(
        [auth, user.user],
        () => {
            void ensureUserLoaded();
        },
        { immediate: true }
    );

    const customFields = computed<Record<string, unknown>>(() => {
        const raw = user.user.value?.customFields;
        return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    });

    const isCurrentUserParticipating = computed(() => {
        const profession = auth.value?.profession;
        return typeof profession === 'string' ? BONUS_PROFESSIONS.includes(profession) : false;
    });

    const points = computed(() => {
        if (!isCurrentUserParticipating.value) return null;

        if (!user.user.value) return null;

        const rawPoints = customFields.value.custom_middleware_customer_experience_score_available;
        if (typeof rawPoints !== 'string') return null;

        const parsed = parseInt(rawPoints, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    });

    const pointsFormatted = computed(() =>
        points.value !== null ? new Intl.NumberFormat('de-DE').format(points.value) : '???'
    );
    const isLoading = computed(
        () => isCurrentUserParticipating.value && points.value === null && isRefreshingUser.value
    );

    const { t } = useI18n();

    return {
        isLoading,
        isCurrentUserParticipating,
        currentUsersPoints: {
            raw: points,
            formatted: pointsFormatted,
            unit: t('account.bonus.unit')
        }
    };
};
